import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Helmet } from 'react-helmet';

import moment from 'moment';
import { change, submit } from 'redux-form';

import { withStyles } from '@material-ui/core/styles';

import Layout from 'components/common/Layout';
import Progress from 'components/common/Progress';

import ConnectedOrdersToolbar from 'components/workflow/ConnectedOrdersToolbar';
import OrdersTable, { OrdersTableNoBody } from 'components/workflow/OrdersTable';
import Snackbar from 'components/common/Snackbar';
import Button from 'components/common/Button';
import StickyToolbar from 'components/common/StickyToolbar';

import {
  clearConnectedOrders,
  fetchConnectedOrders,
} from 'redux/modules/connectedOrders';
import { addRouterParamsPageSettings } from 'redux/modules/routerParams';
import { addNotification } from 'redux/modules/notifications';

import objectDiff from 'utils/objectDiff';
import formatPrice from 'utils/formatPrice';
import formatSinglePlural from 'utils/formatSinglePlural';

import { appTitle, titleSeparator } from 'config/helmetSettings';
import dateFormat from 'config/dateFormat';
import timeDateFormat from 'config/timeDateFormat';
import timeFormat from 'config/timeFormat';

const styles = () => ({
  table: {
    paddingTop: 151,
  },
});

class ConnectedOrders extends Component {
  constructor(props) {
    super(props);

    this.state = {
      groupedUpdateSnackbarOpen: false,
      groupedUpdateValues: null,
      groupedUpdateOrderID: null,
      groupedUpdateEmptyDriverIDs: null,
      groupedUpdateEmptyDateOfDeliveryIDs: null,
    };

    this.groupedUpdate = false;

    this.onFormChange = this.onFormChange.bind(this);
    this.onFormSubmitSuccess = this.onFormSubmitSuccess.bind(this);
    this.onFormTextFieldBlur = this.onFormTextFieldBlur.bind(this);
    this.onGroupedUpdateConfirm = this.onGroupedUpdateConfirm.bind(this);
    this.onGroupedUpdateSnackbarClose = this.onGroupedUpdateSnackbarClose.bind(this);
  }

  componentWillMount() {
    this.props.actions.clearConnectedOrders();
  }

  componentDidMount() {
    const { actions, match } = this.props;
    actions.fetchConnectedOrders({
      triggerLoading: true,
      orderLineID: match.params.id.split(',').map(item => Number(item)),
    });
  }

  onFormSubmitSuccess(values) {
    const { orderLines, actions } = this.props;
    const { groupedUpdateEmptyDriverIDs, groupedUpdateEmptyDateOfDeliveryIDs } = this.state;
    let emptyDriverIDs = values
      .filter(orderLine => (orderLine['Workflow Status'] && orderLine['Workflow Status'].filter(statusItem =>
        statusItem['Workflow State'].ID === 2).length > 0
        && !orderLine.Driver.ID))
      .map(orderLine => orderLine.ID);
    let emptyDateOfDeliveryIDs = values
      .filter(orderLine => (orderLine['Workflow Status'] && orderLine['Workflow Status'].filter(statusItem =>
        statusItem['Workflow State'].ID === 2).length > 0
        && !orderLine['Date of Delivery']))
      .map(orderLine => orderLine.ID);
    if (groupedUpdateEmptyDriverIDs) {
      emptyDriverIDs = [
        ...emptyDriverIDs,
        ...groupedUpdateEmptyDriverIDs,
      ];
    }
    if (groupedUpdateEmptyDateOfDeliveryIDs) {
      emptyDateOfDeliveryIDs = [
        ...emptyDateOfDeliveryIDs,
        ...groupedUpdateEmptyDateOfDeliveryIDs,
      ];
    }
    if (this.state.groupedUpdateValues) {
      this.setState({
        groupedUpdateSnackbarOpen: true,
        groupedUpdateEmptyDriverIDs: emptyDriverIDs.length > 0 ? emptyDriverIDs : null,
        groupedUpdateEmptyDateOfDeliveryIDs: emptyDateOfDeliveryIDs.length > 0 ? emptyDateOfDeliveryIDs : null,
      });
    } else if (emptyDriverIDs.length > 0 || emptyDateOfDeliveryIDs.length > 0) {
      const notificationMessage = [
        emptyDriverIDs.length > 0
          ? `${formatSinglePlural({
            quantity: emptyDriverIDs.length,
            single: 'Code',
            plural: 'Codes',
          })} ${orderLines
              .filter(item => emptyDriverIDs.indexOf(item.ID) !== -1)
              .map(item => item['HCPC S Code']['HCPC S Code'])
              .join(', ')} ${formatSinglePlural({
            quantity: emptyDriverIDs.length,
            single: 'has',
            plural: 'have',
          })} missing Driver information`
          : null,
        emptyDateOfDeliveryIDs.length > 0
          ? `${formatSinglePlural({
            quantity: emptyDateOfDeliveryIDs.length,
            single: 'Code',
            plural: 'Codes',
          })} ${orderLines
              .filter(item => emptyDateOfDeliveryIDs.indexOf(item.ID) !== -1)
              .map(item => item['HCPC S Code']['HCPC S Code'])
              .join(', ')} ${formatSinglePlural({
            quantity: emptyDateOfDeliveryIDs.length,
            single: 'has',
            plural: 'have',
          })} missing Date of Delivery information`
          : null,
      ].filter(item => item).join(', ');
      actions.addNotification({
        message: notificationMessage,
        variant: 'info',
      });
      this.setState({
        groupedUpdateEmptyDriverIDs: null,
        groupedUpdateEmptyDateOfDeliveryIDs: null,
      });
    }
  }

  onFormChange(values, dispatch, props, prevValues) {
    const { variant, actions, orders } = this.props;
    const diffValues = objectDiff(values, prevValues);
    const changeFieldName = Object.keys(diffValues)[0];
    const statusChanged = diffValues[changeFieldName]
      && diffValues[changeFieldName].Status !== undefined;
    const assignedUserChanged = diffValues[changeFieldName]
      && diffValues[changeFieldName]['Assigned User ID'];
    const driverChanged = diffValues[changeFieldName]
      && diffValues[changeFieldName]['Driver ID'] !== undefined;
    const placeOfServiceChanged = diffValues[changeFieldName]
      && diffValues[changeFieldName]['Place of SRV'] !== undefined;
    const dateOfDeliveryChanged = diffValues[changeFieldName]
      && diffValues[changeFieldName]['Date of Delivery'] !== undefined;
    const timeOfDeliveryChanged = diffValues[changeFieldName]
      && diffValues[changeFieldName]['Time of Delivery'] !== undefined;
    if (statusChanged) {
      diffValues[changeFieldName].Status = values[changeFieldName].Status;
    }
    Object.keys(values).forEach((orderLine) => {
      if (values[orderLine]['Assigned User ID']
        && values[orderLine]['Selected Assigned User Name']
          !== values[orderLine]['Assigned User Name']) {
        dispatch(props.change(`${orderLine}.Assigned User ID`, ''));
      }

      if (values[orderLine]['Driver ID']
        && values[orderLine]['Selected Driver Name']
          !== values[orderLine]['Driver Name']) {
        dispatch(props.change(`${orderLine}.Driver ID`, ''));
      }
    });

    if (!this.groupedUpdate
      && !props.pristine
      && diffValues[changeFieldName].selected === undefined
      && (diffValues[changeFieldName].Reimbs === undefined)
      && diffValues[changeFieldName]['Reimbs Date'] === undefined
      && diffValues[changeFieldName].orderLineInsuranceParam === undefined
    ) {
      if (statusChanged || assignedUserChanged || driverChanged
          || placeOfServiceChanged || dateOfDeliveryChanged || timeOfDeliveryChanged) {
        const changeOrderLineID = Number(changeFieldName.replace('-orderLine', ''));
        const changeOrder = orders.find(order =>
          order['Order Line'].findIndex(orderLine =>
            orderLine.ID === changeOrderLineID) !== -1);
        if (changeOrder['Order Line'].length > 1) {
          this.setState({
            groupedUpdateValues: {
              ...diffValues[changeFieldName],
              ...diffValues[changeFieldName]['Driver ID'] === '' ? {
                'Driver Name': '',
                'Selected Driver Name': '',
              } : {},
            },
            groupedUpdateOrderID: changeOrder.ID,
          });
        }
        setTimeout(() => {
          actions.submit(`${variant}-ordersForm`);
        }, 100);
      }
    }
  }

  onFormTextFieldBlur(values) {
    const { variant, orders, actions } = this.props;
    const changeFieldName = Object.keys(values)[0];
    const changeOrderLineID = Number(changeFieldName.replace('-orderLine', ''));
    const changeOrder = orders.find(order =>
      order['Order Line'].findIndex(orderLine =>
        orderLine.ID === changeOrderLineID) !== -1);
    actions.submit(`${variant}-ordersForm`);
    if (changeOrder['Order Line'].length > 1) {
      this.setState({
        groupedUpdateValues: values[changeFieldName],
        groupedUpdateOrderID: changeOrder.ID,
      });
    }
  }

  onGroupedUpdateConfirm() {
    const { actions, variant, orders } = this.props;
    const { groupedUpdateValues, groupedUpdateOrderID } = this.state;
    const groupedUpdateOrder = orders.find(order => order.ID === groupedUpdateOrderID);
    this.groupedUpdate = true;
    this.setState({
      groupedUpdateValues: null,
      groupedUpdateSnackbarOpen: false,
    });
    groupedUpdateOrder['Order Line'].forEach((orderLine) => {
      Object.keys(groupedUpdateValues).forEach((fieldName) => {
        if (typeof groupedUpdateValues[fieldName] === 'object') {
          Object.keys(groupedUpdateValues[fieldName]).forEach((nestedFieldName) => {
            if (typeof groupedUpdateValues[fieldName][nestedFieldName] === 'object') {
              Object.keys(groupedUpdateValues[fieldName][nestedFieldName])
                .forEach((innerNestedFieldName) => {
                  actions.change(`${variant}-ordersForm`,
                    `${orderLine.ID}-orderLine.${fieldName}.${nestedFieldName}.${innerNestedFieldName}`,
                    groupedUpdateValues[fieldName][nestedFieldName][innerNestedFieldName]);
                });
            } else {
              actions.change(`${variant}-ordersForm`, `${orderLine.ID}-orderLine.${fieldName}.${nestedFieldName}`, groupedUpdateValues[fieldName][nestedFieldName]);
            }
          });
        } else {
          actions.change(`${variant}-ordersForm`, `${orderLine.ID}-orderLine.${fieldName}`, groupedUpdateValues[fieldName]);
        }
      });
    });
    // Delay to wait multiple async change actions to complete
    setTimeout(() => {
      actions.submit(`${variant}-ordersForm`);
      this.groupedUpdate = false;
    }, 200);
  }

  onGroupedUpdateSnackbarClose() {
    const { orderLines, actions } = this.props;
    const { groupedUpdateEmptyDriverIDs, groupedUpdateEmptyDateOfDeliveryIDs } = this.state;
    const newState = {
      groupedUpdateValues: null,
      groupedUpdateSnackbarOpen: false,
    };
    if ((groupedUpdateEmptyDriverIDs || groupedUpdateEmptyDateOfDeliveryIDs) && !this.groupedUpdate) {
      const notificationMessage = [
        groupedUpdateEmptyDriverIDs.length > 0
          ? `${formatSinglePlural({
            quantity: groupedUpdateEmptyDriverIDs.length,
            single: 'Code',
            plural: 'Codes',
          })} ${orderLines
              .filter(item => groupedUpdateEmptyDriverIDs.indexOf(item.ID) !== -1)
              .map(item => item['HCPC S Code']['HCPC S Code'])
              .join(', ')} ${formatSinglePlural({
            quantity: groupedUpdateEmptyDriverIDs.length,
            single: 'has',
            plural: 'have',
          })} missing Driver information`
          : null,
        groupedUpdateEmptyDateOfDeliveryIDs.length > 0
          ? `${formatSinglePlural({
            quantity: groupedUpdateEmptyDateOfDeliveryIDs.length,
            single: 'Code',
            plural: 'Codes',
          })} ${orderLines
              .filter(item => groupedUpdateEmptyDateOfDeliveryIDs.indexOf(item.ID) !== -1)
              .map(item => item['HCPC S Code']['HCPC S Code'])
              .join(', ')} ${formatSinglePlural({
            quantity: groupedUpdateEmptyDateOfDeliveryIDs.length,
            single: 'has',
            plural: 'have',
          })} missing Date of Delivery information`
          : null,
      ].filter(item => item).join(', ');
      actions.addNotification({
        message: notificationMessage,
        variant: 'info',
      });
      newState.groupedUpdateEmptyDriverIDs = null;
      newState.groupedUpdateEmptyDateOfDeliveryIDs = null;
    }
    this.setState(newState);
  }

  render() {
    const {
      orders,
      count,
      loading,
      variant,
      classes,
    } = this.props;
    const { groupedUpdateSnackbarOpen } = this.state;

    return (
      <Layout>
        <Helmet>
          <title>
            {appTitle} {titleSeparator} Connected Orders
          </title>
        </Helmet>
        <StickyToolbar>
          <ConnectedOrdersToolbar />
          <OrdersTableNoBody
            items={orders}
            variant={variant}
            loading={loading}
            count={count}
          />
        </StickyToolbar>
        {loading > 0 &&
          <Progress fixed />
        }
        <div className={classes.table}>
          <OrdersTable
            items={orders}
            variant={variant}
            form={`${variant}-ordersForm`}
            count={count}
            onChange={this.onFormChange}
            onSubmitSuccess={this.onFormSubmitSuccess}
            onFormTextFieldBlur={this.onFormTextFieldBlur}
            onStockLineCreate={this.onRequest}
            loading={loading}
            initialValues={orders.reduce((ordersAccumulator, currentOrder) => ({
              ...ordersAccumulator,
              ...currentOrder['Order Line']
                .reduce((orderLinesAccumulator, currentOrderLineValue) => ({
                  ...orderLinesAccumulator,
                  [`${currentOrderLineValue.ID}-orderLine`]: {
                    Status: currentOrderLineValue['Workflow Status'].map(item => ({
                      value: item.ID,
                      label: item.Name,
                    })),
                    ...currentOrderLineValue['Assigned User'].ID ? {
                      'Assigned User ID': currentOrderLineValue['Assigned User'].ID,
                      'Assigned User Name': `${currentOrderLineValue['Assigned User']['First Name']} ${currentOrderLineValue['Assigned User']['Last Name']}`,
                      'Selected Assigned User Name': `${currentOrderLineValue['Assigned User']['First Name']} ${currentOrderLineValue['Assigned User']['Last Name']}`,
                    } : {
                      'Assigned User ID': 'unassigned',
                    },
                    ...currentOrderLineValue.Driver.ID ? {
                      'Driver ID': currentOrderLineValue.Driver.ID,
                      'Driver Name': `${currentOrderLineValue.Driver['First Name']} ${currentOrderLineValue.Driver['Last Name']}`,
                      'Selected Driver Name': `${currentOrderLineValue.Driver['First Name']} ${currentOrderLineValue.Driver['Last Name']}`,
                    } : {},
                    ...currentOrderLineValue['Date of Delivery'] ? {
                      'Date of Delivery': moment(currentOrderLineValue['Date of Delivery'], timeDateFormat).format(dateFormat),
                      'Time of Delivery': moment(currentOrderLineValue['Date of Delivery'], timeDateFormat).format(timeFormat),
                    } : {},
                    ...currentOrderLineValue['Place of SRV'] ? {
                      'Place of SRV': currentOrderLineValue['Place of SRV'],
                    } : {},
                    ...currentOrderLineValue.Reimbs && currentOrderLineValue.Reimbs !== 'None' ? { Reimbs: formatPrice(currentOrderLineValue.Reimbs) } : {},
                    ...currentOrderLineValue['Reimbs Date'] ? { 'Reimbs Date': currentOrderLineValue['Reimbs Date'] } : {},
                    ...currentOrderLineValue['Check Book Sheet Link'] && currentOrderLineValue['Check Book Sheet Link'].length > 0
                      ? { 'Check Book Sheet': currentOrderLineValue['Check Book Sheet Link']
                        .reduce((accumulator, currentValue) => ({
                          ...accumulator,
                          [`insuranceID-${currentValue['Insurance ID']}`]: {
                            'Check Number': currentValue['Check Book Sheet']['Check Number'],
                            'Check Book Sheet ID': currentValue['Check Book Sheet'].ID,
                          },
                        }), {}),
                      } : {},
                    ...currentOrderLineValue['Order Line Insurance Param']
                      && currentOrderLineValue['Order Line Insurance Param'].length > 0 ? {
                        orderLineInsuranceParam: currentOrderLineValue['Order Line Insurance Param']
                          .reduce((accumulator, currentValue) => ({
                            ...accumulator,
                            [`insuranceID-${currentValue.Insurance.ID}`]: {
                              ...currentValue.ID ? {
                                ID: currentValue.ID,
                              } : {},
                              ...currentValue.Reimbs ? {
                                Reimbs: formatPrice(currentValue.Reimbs),
                              } : {},
                              ...currentValue['Reimbs Date'] ? {
                                'Reimbs Date': currentValue['Reimbs Date'],
                              } : {},
                            },
                          }), {}),
                      } : {},
                    orderID: currentOrder.ID,
                  },
                }), {}),
            }), {})}
          />
        </div>
        <Snackbar
          open={groupedUpdateSnackbarOpen}
          variant="success"
          message="Order was successfully updated. Apply this update to other order lines?"
          autoHideDuration={3000}
          resumeHideDuration={0}
          onClose={this.onGroupedUpdateSnackbarClose}
          action={(
            <Button
              variant="raised"
              color="primary"
              onClick={this.onGroupedUpdateConfirm}
            >
              Submit
            </Button>
          )}
        />
      </Layout>
    );
  }
}

ConnectedOrders.propTypes = {
  orders: PropTypes.arrayOf(PropTypes.shape({
    ID: PropTypes.number,
  })).isRequired,
  orderLines: PropTypes.arrayOf(PropTypes.shape({
    ID: PropTypes.number,
  })).isRequired,
  count: PropTypes.number.isRequired,
  loading: PropTypes.number.isRequired,
  actions: PropTypes.shape({
    fetchConnectedOrders: PropTypes.func.isRequired,
    clearConnectedOrders: PropTypes.func.isRequired,
    addRouterParamsPageSettings: PropTypes.func.isRequired,
    submit: PropTypes.func.isRequired,
    change: PropTypes.func.isRequired,
    addNotification: PropTypes.func.isRequired,
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  variant: PropTypes.oneOf([
    'connected',
  ]).isRequired,
  classes: PropTypes.shape({
    table: PropTypes.string.isRequired,
  }).isRequired,
};

ConnectedOrders.defaultProps = {
  connectedOrdersOrderLineID: [],
  variant: 'connected',
};

const mapStateToProps = store => ({
  orders: store.connectedOrders.data,
  orderLines: store.connectedOrders.data.reduce((accumulator, currentValue) => ([
    ...accumulator,
    ...currentValue['Order Line'],
  ]), []),
  count: store.connectedOrders.count,
  loading: store.connectedOrders.loading,
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({
    fetchConnectedOrders,
    clearConnectedOrders,
    addRouterParamsPageSettings,
    submit,
    change,
    addNotification,
  }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(
  withStyles(styles)(ConnectedOrders),
);



// WEBPACK FOOTER //
// ./src/pages/workflow/ConnectedOrders.js